import React from "react";
import { useForm, useFormState } from "react-hook-form";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import CategoryIcon from "@mui/icons-material/Category";
import Edit from "@mui/icons-material/Edit";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";

import arrayIncludesAny from "../../shared/arrayIncludesAny.js";
import axios from "../axiosClient.js";
import useAuth0Roles from "../hooks/auth0Roles.jsx";
import useAlertSnackbar from "../hooks/useAlertSnackbar.jsx";
import Alert from "./common/Alert.jsx";
import CancelButton from "./common/CancelButton.jsx";
import DialogTitleBar from "./common/DialogTitleBar.jsx";
import FormAutocompleteInput from "./common/FormAutocompleteInput.jsx";
import FormStringInput from "./common/FormStringInput.jsx";
import ConfirmAction from "./ConfirmAction.jsx";

const salesBucketOptions = [
  { id: "Bronze", name: "Bronze" },
  { id: "Silver", name: "Silver" },
  { id: "Gold", name: "Gold" },
  { id: "Platinum", name: "Platinum" },
];

function PartForm({
  // Props
  part,
  plcId,
  mutate,
}) {
  // This component doesn't care about the current state of the success message
  const [, setSuccess] = useAlertSnackbar("success", 30_000);

  const formTitle = React.useMemo(
    () => (part?.id ? "Update Product Part Number" : "Add Product Part Number"),
    [part?.id]
  );
  const disablePropagation = React.useCallback((event) => {
    event.stopPropagation();
  }, []);

  //---------------------------------------------------------------------------
  // Modal state management
  //---------------------------------------------------------------------------
  const [open, setOpen] = React.useState(false);

  //---------------------------------------------------------------------------
  // Error alerting state management
  //---------------------------------------------------------------------------
  const [error, setError] = React.useState(null);

  //---------------------------------------------------------------------------
  // Update button state management
  //---------------------------------------------------------------------------
  const [loading, setLoading] = React.useState(false);

  //---------------------------------------------------------------------------
  // Form Submission
  //---------------------------------------------------------------------------
  const defaultValues = React.useMemo(
    () => ({
      number: part?.number || "",
      description: part?.description || "",
      unitOfMeasure: part?.unitOfMeasure || "",
      basePrice: part?.basePrice || "",
      listPrice: part?.listPrice || "",
      salesBucket: salesBucketOptions.find((option) => option.id === part?.salesBucket) || "",
      repStock: part?.repStock || "",
      shelfStock: part?.shelfStock || "",
    }),
    [
      part?.basePrice,
      part?.description,
      part?.listPrice,
      part?.number,
      part?.repStock,
      part?.salesBucket,
      part?.shelfStock,
      part?.unitOfMeasure,
    ]
  );

  const { handleSubmit, control, reset } = useForm({
    defaultValues,
    shouldUnregister: true,
  });

  const { isDirty } = useFormState({ control });

  const onSubmit = React.useCallback(
    async (data) => {
      // Clean up the form data
      if (data.basePrice !== "") {
        data.basePrice = Number(data.basePrice);
      } else {
        data.basePrice = undefined;
      }
      if (data.listPrice !== "") {
        data.listPrice = Number(data.listPrice);
      } else {
        data.listPrice = undefined;
      }
      if (data.salesBucket?.id) {
        data.salesBucket = data.salesBucket.id;
      } else {
        data.salesBucket = null;
      }
      if (data.repStock !== "") {
        data.repStock = Number(data.repStock);
      } else {
        data.repStock = null;
      }
      if (data.shelfStock !== "") {
        data.shelfStock = Number(data.shelfStock);
      } else {
        data.shelfStock = null;
      }
      data.plcId = plcId;

      // Disable the submit button and display the spinner
      setLoading(true);

      try {
        if (part) {
          const partUrlComponent = encodeURIComponent(part.id);

          await axios({
            method: "PATCH",
            url: `/api/parts/${partUrlComponent}`,
            data,
          });
        } else {
          await axios({
            method: "POST",
            url: `/api/parts`,
            data,
          });
        }

        // Close the modal
        setOpen(false);

        // Update the reload state to trigger a data re-fetch
        if (mutate) {
          mutate();
        }

        setSuccess("Successfully saved changes!");
      } catch (err) {
        setError(err?.response?.data?.error || err?.message);
      }
      setLoading(false);
    },
    [plcId, part, mutate, setSuccess]
  );

  const handleOpen = React.useCallback(
    (event) => {
      event.stopPropagation();
      reset(defaultValues);
      setOpen(true);
    },
    [defaultValues, reset]
  );
  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  //---------------------------------------------------------------------------
  // Delete Confirmation Form
  //---------------------------------------------------------------------------
  const { roles } = useAuth0Roles();
  const canDelete = React.useMemo(
    () => arrayIncludesAny(roles, ["Scorecard Admin", "Scorecard PLC Admin"]),
    [roles]
  );
  const handleDeleteConfirmation = React.useCallback(async () => {
    if (part) {
      const partUrlComponent = encodeURIComponent(part.id);
      await axios({
        method: "DELETE",
        url: `/api/parts/${partUrlComponent}`,
      });

      // Reset and close the modal
      handleClose();

      // Update the reload state to trigger a data re-fetch
      if (mutate) {
        mutate();
      }
    }
  }, [handleClose, part, mutate]);

  //---------------------------------------------------------------------------
  // Render
  //---------------------------------------------------------------------------
  return (
    <>
      {part?.id ? (
        <IconButton
          color="primary"
          onClick={handleOpen}
          sx={{ mt: 0.5 }}
          data-cy={`edit-plc-part-row-${part?.id}`}
        >
          <Edit />
        </IconButton>
      ) : (
        <IconButton color="secondary" onClick={handleOpen} sx={{ mt: 0.5 }} data-cy="add-plc-part">
          <AddCircleIcon />
        </IconButton>
      )}
      <Dialog
        open={open}
        maxWidth="md"
        fullWidth
        onClick={disablePropagation}
        slotProps={{
          paper: {
            component: "form",
            onSubmit: handleSubmit(onSubmit),
            noValidate: true,
          },
        }}
      >
        <Alert message={error} setMessage={setError} level="error" data-cy="error-alert" />
        <DialogTitleBar title={formTitle} Icon={CategoryIcon} iconColor="secondary" />
        <DialogContent>
          <Grid container spacing={4} sx={{ alignItems: "center" }}>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormStringInput
                control={control}
                label="Part Number"
                name="number"
                required
                rules={{
                  required: "Please enter a part number",
                  maxLength: {
                    value: 15,
                    message: "Part number should be less than 16 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <FormStringInput
                control={control}
                label="Description"
                name="description"
                required
                rules={{
                  required: "Please enter a description for this part number",
                  maxLength: {
                    value: 127,
                    message: "Product description should be less than 128 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="Unit of Measure"
                name="unitOfMeasure"
                required
                rules={{
                  required: "Please enter a unit of measure for this part number",
                  maxLength: {
                    value: 127,
                    message: "Units of measure should be less than 128 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="Base Price"
                name="basePrice"
                type="number"
                units="$"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput
                control={control}
                label="List Price"
                name="listPrice"
                type="number"
                units="$"
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormAutocompleteInput
                control={control}
                label="Sales Bucket"
                name="salesBucket"
                options={salesBucketOptions}
                rules={{
                  maxLength: {
                    value: 32,
                    message: "Sales buckets should be less than 32 characters",
                  },
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput control={control} label="Rep Stock (total)" name="repStock" type="number" />
            </Grid>
            <Grid
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <FormStringInput control={control} label="Shelf Stock" name="shelfStock" type="number" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={{ m: 2, marginRight: "auto" }}>
            {!!part && canDelete && (
              <ConfirmAction
                actionName="Delete"
                objectName="Part"
                handleConfirmation={handleDeleteConfirmation}
                confirmKeyword={part.number}
              />
            )}
          </Box>
          <Box sx={{ m: 2 }}>
            <CancelButton color="secondary" isDirty={isDirty} onClick={handleClose}>
              Cancel
            </CancelButton>
          </Box>
          <Box sx={{ m: 2 }}>
            <Button
              id="submitDeviceVariantButton"
              disabled={loading}
              variant="contained"
              color="secondary"
              loading={loading}
              type="submit"
            >
              {part?.id ? "Update" : "Add"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PartForm;
